import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Modal } from "bootstrap";
import { getImageUrlHelper, truncateText } from "../../helper/helper";
import { createMedia, deleteMedia, getMedia, updateMedia } from "../../redux/media/mediaSlice";
import useDebounce from "../../hook/useDebounce";
import { processFileUpload, FILE_TYPES } from "../../utils/fileUploadHelper";
import { useLocationFilter } from "../../context/LocationContext";
import LocationSelectComponent from "../../components/common/LocationSelectComponent";
import PaginationHelper from "../../helper/PaginationHelper";

const initialState = {
    location: null,
    title: "",
    description: "",
    link: "",
    file: null,
};
const DESC_MAX = 255;

const MediaLayer = () => {

    const dispatch = useDispatch();
    const { selectedLocation } = useLocationFilter();

    const { media = [], total = 0 } = useSelector((state) => state.media);

    const [search, setSearch] = useState("");
    const [entries, setEntries] = useState(50);
    const [currentPage, setCurrentPage] = useState(1);
    const debouncedSearch = useDebounce(search, 1000);

    const [formData, setFormData] = useState(initialState);
    const [isEdit, setIsEdit] = useState(false);
    const [editId, setEditId] = useState(null);
    const [deleteId, setDeleteId] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    useEffect(() => {
        dispatch(getMedia({
            page: currentPage,
            limit: entries,
            search: debouncedSearch,
            locationId: selectedLocation?.value || "",
        }));
    }, [dispatch, currentPage, entries, debouncedSearch, selectedLocation]);

    const filtered = media.filter((m) =>
        m.title?.toLowerCase().includes(search.toLowerCase())
    );

    const totalEntries = total;
    const totalPages = Math.ceil(totalEntries / entries);

    const startIndex = (currentPage - 1) * entries;
    const endIndex = startIndex + entries;

    const paginated = filtered.slice(startIndex, endIndex);

    const openCreate = () => {
        setFormData(initialState);
        setImagePreview(null);
        setIsEdit(false);
    };

    const openEdit = (m) => {
        setFormData({
            location: {
                value: m.locationId,
                label: m.locationName,
            },
            title: m.title || "",
            description: m.description || "",
            link: m.link || "",
            file: null,
        });

        setImagePreview(getImageUrlHelper(m.path));
        setEditId(m.id);
        setIsEdit(true);
    };

    const handleSubmit = () => {
        const { title, description, link, file } = formData;
        if (!formData.location) {
            toast.error("Location is required");
            return;
        }
        if (!title || !title.trim()) {
            toast.error("Title is required");
            return;
        }

        if (!description || !description.trim()) {
            toast.error("Description is required");
            return;
        }

        if (description.length > DESC_MAX) {
            toast.error(`Description max ${DESC_MAX} characters`);
            return;
        }

        if (link && !/^https?:\/\/.+\..+/.test(link)) {
            toast.error("Enter valid URL (http/https)");
            return;
        }

        if (!file && !isEdit) {
            toast.error("Image is required");
            return;
        }

        const fd = new FormData();
        fd.append("locationId", formData.location.value);
        fd.append("title", title);
        fd.append("description", description);
        if (link) fd.append("link", link);
        if (file) fd.append("file", file);

        const action = isEdit
            ? updateMedia({ id: editId, data: fd })
            : createMedia(fd);

        dispatch(action)
            .unwrap()
            .then((res) => {
                const message =
                    res?.message ||
                    res?.data?.message ||
                    "Operation successful";

                toast.success(message);

                dispatch(getMedia({
                    page: currentPage,
                    limit: entries,
                    search: debouncedSearch,
                    locationId: selectedLocation?.value || "",
                }));

                document.querySelector("#exampleModal .btn-close")?.click();
            })
            .catch((err) => {
                toast.error(
                    err?.response?.data?.message ||
                    err?.message ||
                    "Something went wrong"
                );
            });
    };
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const processedFile = await processFileUpload(file, {
            allowedTypes: FILE_TYPES.IMAGE,
        });

        if (!processedFile) return;

        setFormData({
            ...formData,
            file: processedFile,
        });

        setImagePreview(URL.createObjectURL(processedFile));
    };

    return (
        <>
            <div className="card h-100 p-0 radius-12">

                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">

                    <div className="d-flex align-items-center flex-wrap gap-3">

                        <span className="text-md fw-medium text-secondary-light">
                            Show
                        </span>

                        <select
                            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
                            value={entries}
                            onChange={(e) => {
                                setEntries(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                        >
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="150">150</option>
                            <option value="200">200</option>
                        </select>

                        <form className="navbar-search">
                            <input
                                type="text"
                                className="bg-base h-40-px w-auto"
                                placeholder="Search"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                            <Icon icon="ion:search-outline" className="icon" />
                        </form>

                    </div>

                    <button
                        className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                        data-bs-toggle="modal"
                        data-bs-target="#exampleModal"
                        onClick={openCreate}
                    >
                        <Icon icon="ic:baseline-plus" />
                        Add Media
                    </button>

                </div>

                {/* TABLE */}
                <div className="card-body p-12">
                    <div className="table-responsive">

                        <table className="table bordered-table sm-table">

                            <thead>
                                <tr>
                                    <th>S.L</th>
                                    <th>Image</th>
                                    <th>Title</th>
                                    <th>Description</th>
                                    <th>Link</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>

                            <tbody>

                                {media.map((m, index) => (
                                    <tr key={m.id}>
                                        <td>{startIndex + index + 1}</td>
                                        <td>
                                            <img
                                                src={getImageUrlHelper(m.path)}
                                                alt="media"
                                                className="w-40-px h-40-px object-fit-cover radius-8"
                                            />
                                        </td>
                                        <td>{truncateText(m.title, 30)}</td>
                                        <td>{truncateText(m.description, 40)}</td>
                                        <td>{truncateText(m.link || "-", 30)}</td>

                                        <td className="text-center">

                                            <div className="d-flex gap-10 justify-content-center">

                                                <button
                                                    className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                    data-bs-toggle="modal"
                                                    data-bs-target="#exampleModal"
                                                    onClick={() => openEdit(m)}
                                                >
                                                    <Icon icon="lucide:edit" />
                                                </button>

                                                <button
                                                    className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                    data-bs-toggle="modal"
                                                    data-bs-target="#deleteModal"
                                                    onClick={() => setDeleteId(m.id)}
                                                >
                                                    <Icon icon="fluent:delete-24-regular" />
                                                </button>

                                            </div>

                                        </td>
                                    </tr>
                                ))}

                            </tbody>

                        </table>

                    </div>
                    {/* PAGINATION */}
                    <PaginationHelper
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        totalItems={total}
                        pageSize={entries}
                    />

                </div>
            </div>

            {/* MODAL SAME STRUCTURE */}
            <div className="modal fade" id="exampleModal">
                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content radius-16">

                        <div className="modal-header">
                            <h5>{isEdit ? "Update Media" : "Add Media"}</h5>
                            <button className="btn-close" data-bs-dismiss="modal" />
                        </div>

                        <div className="modal-body">

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <LocationSelectComponent
                                        label="Location"
                                        required
                                        value={formData.location}
                                        onChange={(val) =>
                                            setFormData({ ...formData, location: val })
                                        }
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label>Title <span className="text-danger">*</span></label>
                                    <input
                                        className="form-control"
                                        value={formData.title}
                                        onChange={(e) =>
                                            setFormData({ ...formData, title: e.target.value })
                                        }
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label>
                                        Description <span className="text-danger">*</span>{" "}
                                        <small className="fst-italic">(Max 255 chars)</small>
                                    </label>
                                    <textarea
                                        className="form-control"
                                        rows={5}
                                        maxLength={255}
                                        value={formData.description}
                                        onChange={(e) =>
                                            setFormData({ ...formData, description: e.target.value })
                                        }
                                    />
                                </div>

                                <div className="col-md-6 mb-3">

                                    <label>Upload Image <span className="text-danger">*</span></label>

                                    <label
                                        htmlFor="mediaUpload"
                                        className="border radius-8 border-dashed d-flex align-items-center justify-content-center"
                                        style={{ height: "150px", cursor: "pointer" }}
                                    >
                                        {imagePreview ? (
                                            <img
                                                src={imagePreview}
                                                className="w-100 h-100 object-fit-contain radius-8"
                                            />
                                        ) : (
                                            <div className="text-center">
                                                <Icon icon="solar:camera-outline" />
                                                <p><small>(Only image allowed)</small></p>
                                            </div>
                                        )}
                                    </label>

                                    <input
                                        id="mediaUpload"
                                        type="file"
                                        hidden
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={handleFileChange}
                                    />

                                </div>

                                <div className="col-md-6 mb-3">
                                    <label>Link <small>(optional)</small></label>
                                    <input
                                        className="form-control"
                                        placeholder="https://example.com"
                                        value={formData.link}
                                        onChange={(e) =>
                                            setFormData({ ...formData, link: e.target.value })
                                        }
                                    />
                                </div>

                            </div>

                            <div className="text-end">
                                <button className="btn btn-primary" onClick={handleSubmit}>
                                    {isEdit ? "Update" : "Create"}
                                </button>
                            </div>

                        </div>

                    </div>
                </div>
            </div>

            {/* DELETE MODAL SAME */}
            <div className="modal fade" id="deleteModal">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content radius-16">

                        <div className="modal-header">
                            <h5>Delete Media</h5>
                            <button className="btn-close" data-bs-dismiss="modal" />
                        </div>

                        <div className="modal-body text-center">
                            <p>Are you sure?</p>

                            <button
                                className="btn btn-danger"
                                onClick={() => {
                                    dispatch(deleteMedia(deleteId))
                                        .unwrap()
                                        .then((res) => {
                                            toast.success(res?.message || "Deleted successfully");

                                            dispatch(getMedia({
                                                page: currentPage,
                                                limit: entries,
                                                search: debouncedSearch,
                                                locationId: selectedLocation?.value || "",
                                            }));

                                            document.querySelector("#deleteModal .btn-close")?.click();
                                        })
                                        .catch((err) => {
                                            toast.error(
                                                err?.response?.data?.message ||
                                                err?.message ||
                                                "Delete failed"
                                            );
                                        });
                                }}
                            >
                                Delete
                            </button>

                        </div>

                    </div>
                </div>
            </div>

        </>
    );
};

export default MediaLayer;